// src/features/executive/insight/drill/NewPatientTrendDrill.tsx
import { Box, Typography, Tooltip } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Bar } from 'react-chartjs-2'
import { useNewPatientDetail } from '../hooks/use-new-patient-detail'
import DrillTable from 'features/executive/components/DrillTable/DrillTable'
import type { ExecutiveState } from 'app/slice/executive.slice'
import { useRegisterDrillExport } from 'features/executive/components/DrillDrawer/DrillExportContext'
import { formatDateThaiShort } from 'utils/executive.utils'

export default function NewPatientTrendDrill() {
  const { t } = useTranslation()
  const stack = useSelector((s: { executive: ExecutiveState }) => s.executive.drillStack)
  const top = stack[stack.length - 1]
  const { data, loading, error, refetch } = useNewPatientDetail(top?.scopedFilters)

  const widgetTitle = top?.label ?? t('EXECUTIVE.INSIGHT.NEW_TREND.TITLE')
  useRegisterDrillExport(
    data ? { drillId: 'new-patient-trend', data: data.rows } : null,
    'insight.new-patient-detail',
    widgetTitle
  )

  const rows = data?.rows ?? []
  const chartData = {
    labels: rows.map((r) => formatDateThaiShort(r.date)),
    datasets: [{
      label: t('EXECUTIVE.INSIGHT.NEW_TREND.TITLE'),
      data: rows.map((r) => r.count),
      backgroundColor: '#2a8d9c',
    }],
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {top?.label ?? t('EXECUTIVE.INSIGHT.NEW_TREND.TITLE')}
      </Typography>
      <Box sx={{ height: 260, mb: 3 }}>
        <Bar
          data={chartData as any}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            plugins: { legend: { display: false } },
            scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
          }}
        />
      </Box>
      <DrillTable
        rows={rows}
        columns={[
          { key: 'date', label: t('EXECUTIVE.DRILL.DATE', 'Date'), render: (r) => formatDateThaiShort(r.date) },
          { key: 'count', label: t('EXECUTIVE.INSIGHT.NEW_TREND.COUNT', 'New patients'), align: 'right', render: (r) => r.count },
          {
            key: 'names',
            label: t('EXECUTIVE.INSIGHT.NEW_TREND.SAMPLE_NAMES', 'Sample names'),
            render: (r) => (
              <Tooltip title={r.patientNames.join(', ')}>
                <span>{r.patientNames.slice(0, 3).join(', ')}{r.patientNames.length > 3 ? '…' : ''}</span>
              </Tooltip>
            ),
          },
        ]}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    </Box>
  )
}
